'use client';

import React, { useEffect, useState } from 'react';
import PageLayout from '../PageLayout';
import NewsletterForm from './newsletterForm';
import { useTranslation } from 'react-i18next';
import { useParams } from 'next/navigation';
import { defaultLang, SUPPORTED } from '../../config/languages';

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4 4" />
  </svg>
);

const Support = () => {
  const [faqs, setFaqs] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [search, setSearch] = useState('');
  const { t } = useTranslation(['support', 'newsletter']);
  const { lang = defaultLang } = useParams();
  const activeLang = SUPPORTED.includes(lang) ? lang : defaultLang;

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const response = await fetch(`/api/faq/faq_${activeLang}.json`);
        if (!response.ok) throw new Error('FAQ locale not found');
        setFaqs(await response.json());
      } catch {
        try {
          const fallback = await fetch('/api/faq/faq_en.json');
          setFaqs(await fallback.json());
        } catch (error) {
          console.error('Failed to load FAQs:', error);
        }
      }
    };

    loadFaqs();
  }, [activeLang]);

  const filteredFaqs = faqs.filter((faq) => {
    const query = search.trim().toLowerCase();
    return !query || faq.q.toLowerCase().includes(query) || faq.a.toLowerCase().includes(query);
  });

  const canonical = `https://www.adcast.app${activeLang === 'en' ? '' : '/' + activeLang}/support`;

  return (
    <PageLayout
      presentLikeHome
      title={t('pageTitle')}
      metaDescription={t('pageMetaDescription')}
      canonical={canonical}
    >
      <link rel="stylesheet" href="/assets/css/blog-layout-2.css" />
      <link rel="stylesheet" href="/assets/css/support-layout-2.css" />

      <main className="support-v2">
        <section className="support-v2__hero">
          <div className="support-v2__container">
            <p className="support-v2__eyebrow">{t('support_label')}</p>
            <h1>{t('title')}</h1>
            <p className="support-v2__intro">{t('description')}</p>

            <label className="support-v2__search">
              <SearchIcon />
              <span className="sr-only">{t('search_placeholder')}</span>
              <input
                type="search"
                placeholder={t('search_placeholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} aria-label={t('clear_search')}>×</button>
              )}
            </label>
          </div>
        </section>

        <section className="support-v2__body">
          <div className="support-v2__container support-v2__grid">
            <div className="support-v2__faq">
              <div className="support-v2__section-heading">
                <div>
                  <span>{filteredFaqs.length} {t('questions', { defaultValue: 'questions' })}</span>
                  <h2>{t('faq_title', { defaultValue: 'Frequently asked questions' })}</h2>
                </div>
              </div>

              {faqs.length === 0 ? (
                <div className="support-v2__empty">{t('loadingFaqs')}</div>
              ) : filteredFaqs.length === 0 ? (
                <div className="support-v2__empty">{t('no_results')}</div>
              ) : (
                <div className="support-v2__accordion">
                  {filteredFaqs.map((faq, index) => {
                    const isOpen = activeIndex === index;
                    return (
                      <article className={`support-v2__item${isOpen ? ' is-open' : ''}`} key={faq.id}>
                        <button
                          type="button"
                          className="support-v2__question"
                          aria-expanded={isOpen}
                          aria-controls={`support-answer-${faq.id}`}
                          onClick={() => setActiveIndex(isOpen ? null : index)}
                        >
                          <span>{faq.q}</span>
                          <i aria-hidden="true" />
                        </button>
                        {isOpen && (
                          <div className="support-v2__answer" id={`support-answer-${faq.id}`}>
                            <p>{faq.a}</p>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>

            <aside className="support-v2__aside">
              <section className="support-v2__contact">
                <span className="support-v2__card-label">{t('contact_label', { defaultValue: 'Direct support' })}</span>
                <h2>{t('contact_title', { defaultValue: 'Still need help?' })}</h2>
                <p>{t('contact_description', { defaultValue: 'Send us a message and our team will help you with your AdCast account or displays.' })}</p>
                <a href="mailto:help@adcast.app">
                  help@adcast.app <span aria-hidden="true">→</span>
                </a>
              </section>

              <section className="support-v2__workspace">
                <span className="support-v2__card-label">{t('workspace_label', { defaultValue: 'AdCast Workspace' })}</span>
                <h2>{t('workspace_title', { defaultValue: 'Ready to put your screens to work?' })}</h2>
                <p>{t('workspace_description', { defaultValue: 'Create your first display and start publishing content in minutes.' })}</p>
                <a href="https://workspace.adcast.app/auth/signup">
                  {t('workspace_cta', { defaultValue: 'Get started' })} <span aria-hidden="true">→</span>
                </a>
              </section>

              <section className="support-v2__newsletter">
                <div className="support-v2__newsletter-mark" aria-hidden="true">✦</div>
                <h2>{t('newsletter:newsletterTitle')}</h2>
                <p>{t('newsletter:newsletterDescription')}</p>
                <NewsletterForm />
              </section>
            </aside>
          </div>
        </section>
      </main>
    </PageLayout>
  );
};

export default Support;
